import { useMutation, useQuery } from '@apollo/client'
import { COUNTRY_LIST, PROVINCES_LIST, TIMEZONE_OPTIONS } from 'constants'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import {
  Box,
  Button,
  CardBox,
  Fields,
  FormControl,
  Icons,
  InputLabel,
  Link,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  Tooltip,
  useAlert,
} from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'
import { detectBrowserTimezone } from '../../hooks'

export function ProfileForm() {
  const { data: { me } = {} } = useQuery(queries.user.me)
  const {
    setHasUnsavedChanges: trackUnsavedChanges,
    profileFormRef,
    onSubmitSuccess: onSuccess,
    onSubmitFail: onFail,
    donorFundId,
  } = useOutletContext()
  const { email, profile, timezone: userTimezone } = me ?? {}
  const fundProfile = me?.funds?.find((f) => f.id === donorFundId)?.fundProfile
  const address = fundProfile?.address
  const isCharityUser = (me?.charityUsers || []).length > 0
  const hasRealDonorFunds = (me?.funds || []).some((f) => !f.name?.startsWith('charity-'))
  const isPureCharityUser = isCharityUser && !hasRealDonorFunds
  const firstLogin = !address && !isPureCharityUser
  const { state } = useLocation()
  const { afterLogin } = state ?? {}
  const navigate = useNavigate()
  const { register, handleSubmit, formState, setError, getValues, setValue, watch, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      email: email ?? '',
      phone: profile?.phone ?? '',
      timezone: userTimezone ?? 'America/Vancouver',
      firstName: profile?.firstName ?? '',
      middleInitials: profile?.middleInitials ?? '',
      lastName: profile?.lastName ?? '',
      address: {
        country: address?.country ?? 'Canada',
        province: address?.province ?? '',
        city: address?.city ?? '',
        lineOne: address?.lineOne ?? '',
        lineTwo: address?.lineTwo ?? '',
        postalCode: address?.postalCode ?? '',
      },
    },
  })
  const { isDirty, isSubmitting, errors = {} } = formState

  // Subscribe to these fields so the component re-renders when the controlled
  // Selects below change (e.g. to recompute countryIsCanada)
  watch('address.country')
  watch('address.province')
  watch('timezone')

  useEffect(() => {
    trackUnsavedChanges(isDirty)
  }, [isDirty, trackUnsavedChanges])

  const countryIsCanada =
    getValues('address.country') === 'Canada' ||
    getValues('address.country') === 'CAN' ||
    getValues('address.country') === 'CA'

  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [updateProfile] = useMutation(mutations.user.updateProfile, {
    onCompleted: () => {
      if (firstLogin) {
        navigate(afterLogin ?? '/funds')
      } else {
        setAlert({ message: 'Profile Updated', severity: 'success' })
      }
      // Reset form dirty state with current values as new defaults
      reset(getValues())
      onSuccess?.()
    },
    onError: (serverErrors) => {
      parseAndSetFormErrors(serverErrors, setError)
      setAlert({ message: 'Update Profile Failed', severity: 'error' })
      onFail?.()
    },
    refetchQueries: [{ query: queries.user.me }],
  })

  const onSubmit = async (data) => {
    // Resolve auto-detect to actual browser timezone before submitting
    const submissionData = {
      ...data,
      timezone: data.timezone === 'auto' ? detectBrowserTimezone() : data.timezone,
    }
    updateProfile({
      variables: { data: submissionData },
    })
  }

  const onInvalid = () => {
    // Called when form validation fails
    onFail?.()
  }

  return (
    <CardBox>
      <Text.H4>Personal Details</Text.H4>
      <br />
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} ref={profileFormRef}>
        <Stack>
          <Text.H6>Contact</Text.H6>
          <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <Fields.Text
              inputProps={{
                'data-testid': 'email',
                style: {
                  color: '#333333',
                  WebkitTextFillColor: '#333333',
                },
              }}
              type="text"
              label="Email *"
              error={!!errors.email}
              helperText={errors?.email?.message}
              defaultValue={email}
              {...register('email', { ...validations.email })}
              disabled
            />
            <Button
              sx={{
                position: 'absolute',
                right: '1%',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20%',
                maxHeight: '80%',
              }}
              onClick={() => navigate('../security')}
            >
              Change Email
            </Button>
          </Box>

          <Fields.Text
            inputProps={{ 'data-testid': 'phoneNumber' }}
            type="text"
            label="Phone *"
            error={!!errors.phone}
            helperText={errors?.phone?.message}
            defaultValue={profile?.phone}
            {...register('phone', { ...validations.required, ...validations.phoneNumber })}
          />

          <Text.H6 sx={{ mt: 3 }}>Timezone</Text.H6>
          <Text.Body sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
            Dates and times throughout the portal will be displayed in your selected timezone.
          </Text.Body>
          <FormControl fullWidth>
            <InputLabel id="timezoneLabel">Timezone</InputLabel>
            <Select
              inputProps={{ 'data-testid': 'timezone' }}
              value={getValues('timezone')}
              onChange={(e) => {
                setValue('timezone', e.target.value, { shouldDirty: true })
              }}
              label="Timezone"
              labelId="timezoneLabel"
              aria-label="Timezone"
              defaultValue={userTimezone ?? 'America/Vancouver'}
              {...register('timezone')}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {fundProfile?.identityKind === 'business' && (
            <Box sx={{ width: '100%' }}>
              <Text.Bold>Primary Contact Person</Text.Bold>
              <Row sx={{ mt: 2 }}>
                <Fields.Text
                  inputProps={{ 'data-testid': 'firstName' }}
                  label="First Name *"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  defaultValue={profile?.firstName}
                  {...register('firstName', { ...validations.required })}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'middleInitial' }}
                  label="Middle Initials"
                  error={!!errors.middleInitials}
                  helperText={errors.middleInitials?.message}
                  defaultValue={profile?.middleInitials}
                  {...register('middleInitials')}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'lastName' }}
                  label="Last Name *"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  defaultValue={profile?.lastName}
                  {...register('lastName', { ...validations.required })}
                />
              </Row>
            </Box>
          )}
          <Tooltip title="Address is used for tax receipts only. No physical mail will be sent by GiveWise">
            <Text.H6 sx={{ display: 'flex' }}>
              Address <Icons.Help sx={{ alignSelf: 'center', marginLeft: '.5rem' }} />
            </Text.H6>
          </Tooltip>
          <FormControl fullWidth>
            <InputLabel id="countryLabel">Country *</InputLabel>
            <Select
              inputProps={{ 'data-testid': 'country' }}
              value={getValues('address.country')}
              onChange={(e) => {
                setValue('address.country', e.target.value, { shouldValidate: true })
              }}
              label="Country *"
              labelId="countryLabel"
              aria-label="Country *"
              error={!!errors.address?.country}
              helpertext={errors?.address?.country?.message}
              defaultValue={address?.country ?? 'Canada'}
              {...register('address.country', { ...validations.required })}
            >
              {COUNTRY_LIST.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {countryIsCanada && (
            <FormControl fullWidth>
              <InputLabel id="provinceLabel">Province *</InputLabel>
              <Select
                inputProps={{ 'data-testid': 'province' }}
                value={getValues('address.province')}
                onChange={(e) => {
                  setValue('address.province', e.target.value, { shouldValidate: true })
                }}
                label="Province *"
                labelId="provinceLabel"
                aria-label="Province *"
                error={!!errors.address?.province}
                helpertext={errors?.address?.province?.message}
                defaultValue={address?.province}
                {...register('address.province', { ...validations.required })}
              >
                {PROVINCES_LIST.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!countryIsCanada && (
            <Fields.Text
              inputProps={{ 'data-testid': 'province' }}
              type="text"
              label="Province / State *"
              error={!!errors.address?.province}
              helperText={errors?.address?.province?.message}
              defaultValue={address?.province}
              {...register('address.province', { ...validations.required })}
            />
          )}

          <Fields.Text
            inputProps={{ 'data-testid': 'city' }}
            type="text"
            label="City *"
            error={!!errors.address?.city}
            helperText={errors?.address?.city?.message}
            defaultValue={address?.city}
            {...register('address.city', { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineOne' }}
            type="text"
            label="Address Line One *"
            error={!!errors.address?.lineOne}
            helperText={errors?.address?.lineOne?.message}
            defaultValue={address?.lineOne}
            {...register('address.lineOne', { ...validations.required })}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineTwo' }}
            type="text"
            label="Address Line Two"
            error={!!errors.address?.lineTwo}
            helperText={errors?.address?.lineTwo?.message}
            defaultValue={address?.lineTwo}
            {...register('address.lineTwo')}
          />
          {countryIsCanada && (
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code *"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={address?.postalCode}
              {...register('address.postalCode', { ...validations.required, ...validations.postalCode })}
            />
          )}
          {!countryIsCanada && (
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code / Zip Code *"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={address?.postalCode}
              {...register('address.postalCode', { ...validations.required })}
            />
          )}
          <Text.Body>
            For help with any other changes, please contact:{' '}
            <Link href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</Link>
          </Text.Body>
          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="submit" disabled={isSubmitting} data-testid="updateProfileButton">
              Update Profile
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
    </CardBox>
  )
}
